import type { ReactNode } from 'react';
import Image from 'next/image';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ReviewStats } from '@/types/apartment';

interface ApartmentCardMediaOverlayProps {
  title: string;
  neighborhood: string;
  reviewStats?: ReviewStats | null;
  ratingValue?: number | string | null;
  reviewCount?: number | null;
  platforms?: ReviewStats['platforms'];
  topRight?: ReactNode;
  titleClassName?: string;
}

function formatRating(value: number | string | null | undefined): string | null {
  if (typeof value === 'number') return value.toFixed(1);
  if (typeof value === 'string' && value.trim()) return value;
  return null;
}

export default function ApartmentCardMediaOverlay({
  title,
  neighborhood,
  reviewStats,
  ratingValue,
  reviewCount,
  platforms,
  topRight,
  titleClassName,
}: ApartmentCardMediaOverlayProps) {
  const averageRating = formatRating(ratingValue ?? reviewStats?.avgRating);
  const totalReviews = reviewCount ?? reviewStats?.total;
  const platformCounts = platforms ?? reviewStats?.platforms;
  const showRating = averageRating && typeof totalReviews === 'number' && totalReviews > 0;

  return (
    <>
      <div className="pointer-events-none absolute left-0 right-0 top-0 z-10 bg-gradient-to-b from-black/30 via-black/5 to-transparent p-3 sm:p-4">
        <h3
          className={cn(
            'text-sm font-semibold leading-tight text-white line-clamp-2 sm:text-base',
            topRight ? 'pr-20' : '',
            titleClassName
          )}
          style={{ textShadow: '0 2px 6px rgba(0,0,0,0.55)' }}
        >
          {title}
        </h3>
      </div>

      {topRight ? <div className="pointer-events-none absolute right-3 top-3 z-20">{topRight}</div> : null}

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/35 via-black/5 to-transparent p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]">
            {neighborhood}
          </div>
          {showRating ? (
            <div className="flex flex-shrink-0 items-center gap-2">
              <div className="flex items-center gap-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                <Star className="h-3 w-3 fill-current text-yellow-400" />
                <span className="text-xs font-bold text-white">{averageRating}</span>
                <span className="text-[11px] text-white/85">({totalReviews})</span>
              </div>
              {platformCounts ? (
                <div className="flex items-center gap-1 opacity-80 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                  {platformCounts.Airbnb && platformCounts.Airbnb > 0 ? (
                    <Image
                      src="/icons/airbnb-logo.svg"
                      alt="También en Airbnb"
                      width={14}
                      height={14}
                      className="h-3.5 w-3.5"
                    />
                  ) : null}
                  {((platformCounts['Booking.com'] && platformCounts['Booking.com'] > 0) ||
                    (platformCounts.Booking && platformCounts.Booking > 0)) ? (
                    <Image
                      src="/icons/booking-icon.svg"
                      alt="También en Booking"
                      width={14}
                      height={14}
                      className="h-3.5 w-3.5 rounded-[3px]"
                    />
                  ) : null}
                  {platformCounts.Google && platformCounts.Google > 0 ? (
                    <Image
                      src="/icons/google-symbol.svg"
                      alt="También en Google"
                      width={14}
                      height={14}
                      className="h-3.5 w-3.5 brightness-0 invert"
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
